import {useEffect, useMemo, useState} from 'react';
import {Box, DialogActions, Typography} from '@mui/material';
import {t} from 'i18next';
import {v4 as uuidV4} from 'uuid';
import {GridColDef, GridSortModel} from '@mui/x-data-grid';
import {
  useExcelProcessingErrorsGridData,
  useExportExcelProcessingErrorData,
} from '../../../../hooks/ingestions/useIngestions';
import ArrModal from '../../../common/arr-modal/ArrModal';
import ArrDataGrid from '../../../common/arr-data-grid/ArrDataGrid';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import {ReactComponent as ExportIcon} from '../../../../assets/images/icon-report-export.svg';
import './ExcelProcessingErrorListModal.scss';
import ArrPagination from '../../../common/arr-pagination/ArrPagination';
import ArrButton from '../../../common/arr-button/ArrButton';
import {PAGE_SIZE, ToastType} from '../../../../shared/constants';
import {
  ExcelProcessingErrorGridItemType,
  ToastMessageConfigType,
} from '../../../../api/types/ingestionApi.type';
import CustomNoRowsOverlay from '../CustomNoRowsOverlay/CustomNoRowsOverlay';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';

interface Props {
  nativeExcelIngestionID: number;
  showModal: boolean;
  onClose: () => void;
  showToast: (config: ToastMessageConfigType) => void;
  showExportSuccessToast: (config: ToastMessageConfigType) => void;
}

const defaultSortColumn = {
  field: 'sourceDocID',
  sort: 'desc',
};

interface ErrorListDataType extends ExcelProcessingErrorGridItemType {
  id: string;
}

const stringPath = 'ingestions.excelProcessing.errorModal.';
const toastStringPath = 'ingestions.excelProcessing.toastMsg.';
const excelProcessingStringPath = 'ingestions.excelProcessing.';

const ExcelProcessingErrorListModal: React.FC<Props> = ({
  nativeExcelIngestionID,
  showModal,
  onClose,
  showToast,
  showExportSuccessToast,
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(PAGE_SIZE);
  const [errorListData, setErrorListData] = useState<ErrorListDataType[]>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    defaultSortColumn,
  ] as GridSortModel);

  const {
    data: excelProcessingErrorGridData,
    isLoading: isExcelProcessingErrorGridLoading,
    error: excelProcessingErrorGridError,
    isError: isExcelProcessingErrorGridError,
  } = useExcelProcessingErrorsGridData({
    nativeExcelIngestionID: nativeExcelIngestionID,
    pageNumber: pageNumber,
    pageSize: itemPerPage,
    sortingColumn: sortModel[0]?.field ?? defaultSortColumn.field,
    direction: sortModel[0]?.sort ?? defaultSortColumn.sort,
  });

  const {
    isLoading: isExportLoading,
    isSuccess: isExportSuccess,
    isError: isExportError,
    error: exportError,
    refetch: sendExportReq,
  } = useExportExcelProcessingErrorData({
    nativeExcelIngestionId: nativeExcelIngestionID,
  });

  useEffect(() => {
    const errorsWithId: ErrorListDataType[] = (
      excelProcessingErrorGridData?.data ?? []
    ).map((item) => ({
      ...item,
      id: uuidV4(),
    }));
    setErrorListData(errorsWithId);
  }, [excelProcessingErrorGridData?.data]);

  // Track export report success and show toast for it
  useEffect(() => {
    if (isExportSuccess && !isExportLoading) {
      showExportSuccessToast({
        title: t(toastStringPath + 'exportInitiatedTitle'),
        message: t(toastStringPath + 'exportSuccess'),
        type: ToastType.Success,
      });
    }
  }, [showExportSuccessToast, isExportSuccess, isExportLoading]);

  // Track export report failed and show toast for it
  useEffect(() => {
    if (!isExportLoading && isExportError && exportError) {
      showToast({
        title: t('common.error'),
        message: t(toastStringPath + 'exportExcelProcessingErrorFailed'),
        type: ToastType.Error,
      });
    }
  }, [showToast, isExportError, exportError, isExportLoading]);
  useEffect(() => {
    if (isExcelProcessingErrorGridError && excelProcessingErrorGridError) {
      showToast({
        message: t(excelProcessingStringPath + 'toastMsg.loadingFailed'),
        type: ToastType.Error,
      });
    }
  }, [
    showToast,
    isExcelProcessingErrorGridError,
    excelProcessingErrorGridError,
  ]);

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'sourceDocID',
        headerName: t(stringPath + 'sourceDocId'),
        flex: 0.22,
        minWidth: 160,
      },
      {
        field: 'docID',
        headerName: t('docView.docId'),
        flex: 0.22,
        minWidth: 160,
      },
      {
        field: 'error',
        headerName: t(stringPath + 'error'),
        flex: 0.56,
        headerClassName: 'last-column',
      },
    ];
  }, [t]);

  const renderFooter = () => {
    return (
      <DialogActions
        className="footer-action-container"
        data-testid="excel-processing-error-list-modal-footer"
        id={`${pageNumber}`}
      >
        <Box data-testid="excel-processing-error-list-modal-pagination">
          <ArrPagination
            totalItems={excelProcessingErrorGridData?.totalRecords ?? 0}
            perPage={itemPerPage}
            currentPage={pageNumber}
            onPerPageChange={setItemPerPage}
            onPageChange={setPageNumber}
            showPerPageOptions
            perPageOptions={[10, 25, 50, 100]}
            mode="limited"
            alwaysShow
            isFetching={isExcelProcessingErrorGridLoading}
          />
        </Box>
        <Box
          display="flex"
          className="action-button-box"
          data-testid="excel-processing-error-list-modal-action-buttons"
        >
          <ArrButton
            variant="secondary"
            size="medium"
            onClick={onClose}
            aria-label={t('common.close')}
            dataTestId="excel-processing-error-list-modal-close-button"
          >
            {t('common.close')}
          </ArrButton>
          <ArrButton
            variant="primary"
            size="medium"
            onClick={() => {
              if (!isExportLoading) sendExportReq();
            }}
            aria-label={t('common.export')}
            dataTestId="export-excel-processing-error-button"
            startIcon={<ExportIcon />}
            loading={isExportLoading}
          >
            {t('common.export')}
          </ArrButton>
        </Box>
      </DialogActions>
    );
  };

  const modalTitle = t(stringPath + 'excelProcessingError');
  const hasGridLoadError =
    isExcelProcessingErrorGridError && !isExcelProcessingErrorGridLoading;

  const NoRowsOverlay = () => (
    <Box
      data-testid={
        hasGridLoadError
          ? 'excel-processing-error-list-modal-error-overlay'
          : 'excel-processing-error-list-modal-no-rows'
      }
    >
      <CustomNoRowsOverlay
        className="no-row-container"
        message={
          hasGridLoadError
            ? t(excelProcessingStringPath + 'toastMsg.loadingFailed')
            : t(excelProcessingStringPath + 'excelProcessingErrorNoDataMsg')
        }
      />
    </Box>
  );

  return (
    <ArrModal
      showModal={showModal}
      onClose={onClose}
      hideDefaultActions
      className="excel-processing-error-list-modal"
      dataTestId="excel-processing-error-list-modal"
      headerTitleElement={
        <Box
          display="flex"
          alignItems="center"
          className="title-container"
          data-testid="excel-processing-error-list-modal-title-container"
        >
          <Typography
            className="typo-text headline3_size heading-title-text"
            tabIndex={0}
            data-testid="excel-processing-error-list-modal-title"
          >
            {modalTitle}
          </Typography>
          <ArrTooltipWrapper
            title={modalTitle}
            dataTestId="excel-processing-error-list-modal-info-tooltip"
            trigger={
              <Box
                display="flex"
                data-testid="excel-processing-error-list-modal-info-icon"
              >
                <InfoIcon className="error-list-info-icon" tabIndex={0} />
              </Box>
            }
          >
            {modalTitle}
          </ArrTooltipWrapper>
        </Box>
      }
      footer={renderFooter()}
      autoFocusCloseButton
    >
      <Box
        className="error-list-grid-container"
        data-testid="excel-processing-error-list-modal-grid-container"
      >
        <ArrDataGrid
          className="excel-processing-error-list-grid"
          rows={hasGridLoadError ? [] : errorListData}
          rowCount={
            hasGridLoadError
              ? 0
              : (excelProcessingErrorGridData?.totalRecords ?? 0)
          }
          columns={columns}
          paginationMode="server"
          primaryKey="id"
          loading={isExcelProcessingErrorGridLoading}
          onSortModelChange={setSortModel}
          hideFooter
          hideFooterPagination
          initialState={{
            sorting: {
              sortModel: [defaultSortColumn] as GridSortModel,
            },
          }}
          noRowsOverlay={NoRowsOverlay}
        />
      </Box>
    </ArrModal>
  );
};

export default ExcelProcessingErrorListModal;
